import React from 'react';
import type {
  GanttV5Dependency,
  GanttV5Link,
  GanttV5Model,
  GanttV5Tick,
  GanttV5TimeScale,
} from './types';
import {
  buildGanttV5TaskRects,
  buildGanttV5ConnectionAnchorRects,
  buildGanttV5ExternalConnectionAnchorRects,
  GANTT_V5_HEADER_HEIGHT,
  buildGanttV5GroupRects,
  drawGanttV5InteractionLayer,
  drawGanttV5StaticLayer,
  drawGanttV5TaskLayer,
  hitTestGanttV5Task,
  hitTestGanttV5Group,
  readGanttV5CanvasTheme,
  type GanttV5Viewport,
} from './rendering';
import {
  buildGanttV5ConnectionIndex,
  getRelatedVisibleGanttV5TaskIds,
  getFocusedGanttV5Connections,
  getVisibleGanttV5Connections,
} from './connections';

interface GanttV5CanvasLayersProps {
  rootRef: React.RefObject<HTMLDivElement>;
  model: GanttV5Model;
  dependencies: GanttV5Dependency[];
  links: GanttV5Link[];
  ticks: GanttV5Tick[];
  scale: GanttV5TimeScale;
  viewport: GanttV5Viewport;
  rowHeight: number;
  todayHour?: number;
  selectedTaskIds: ReadonlySet<string>;
  hoveredTaskId: string | null;
  connectionVisibility: 'all' | 'focus';
  previewSpans: Map<string, { start: number; end: number }>;
  onPointerDown: (
    event: React.PointerEvent<HTMLCanvasElement>,
    hit: ReturnType<typeof hitTestGanttV5Task>,
    groupHit: ReturnType<typeof hitTestGanttV5Group>,
  ) => void;
  onPointerMove: (
    event: React.PointerEvent<HTMLCanvasElement>,
    hit: ReturnType<typeof hitTestGanttV5Task>,
  ) => void;
  onPointerUp: (event: React.PointerEvent<HTMLCanvasElement>) => void;
  onPointerLeave: () => void;
  onDoubleClick: (
    event: React.MouseEvent<HTMLCanvasElement>,
    hit: ReturnType<typeof hitTestGanttV5Task>,
  ) => void;
  onContextMenu: (
    event: React.MouseEvent<HTMLCanvasElement>,
    hit: ReturnType<typeof hitTestGanttV5Task>,
    groupHit: ReturnType<typeof hitTestGanttV5Group>,
  ) => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLCanvasElement>) => void;
  onWheel: (event: React.WheelEvent<HTMLCanvasElement>) => void;
  onLayerRender?: (layer: 'static' | 'tasks' | 'interaction', durationMs: number) => void;
}

export const GanttV5CanvasLayers: React.FC<GanttV5CanvasLayersProps> = ({
  rootRef,
  model,
  dependencies,
  links,
  ticks,
  scale,
  viewport,
  rowHeight,
  todayHour,
  selectedTaskIds,
  hoveredTaskId,
  connectionVisibility,
  previewSpans,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerLeave,
  onDoubleClick,
  onContextMenu,
  onKeyDown,
  onWheel,
  onLayerRender,
}) => {
  const staticCanvasRef = React.useRef<HTMLCanvasElement>(null);
  const taskCanvasRef = React.useRef<HTMLCanvasElement>(null);
  const interactionCanvasRef = React.useRef<HTMLCanvasElement>(null);
  const tooltipRef = React.useRef<HTMLDivElement>(null);

  const taskRects = React.useMemo(
    () => buildGanttV5TaskRects(model, scale, viewport, rowHeight),
    [model, rowHeight, scale, viewport],
  );
  const groupRects = React.useMemo(
    () => buildGanttV5GroupRects(model, scale, viewport, rowHeight),
    [model, rowHeight, scale, viewport],
  );
  const connectionIndex = React.useMemo(
    () => buildGanttV5ConnectionIndex(dependencies, links),
    [dependencies, links],
  );
  const visibleConnections = React.useMemo(() => getVisibleGanttV5Connections(
    connectionIndex,
    new Set(taskRects.map((rect) => rect.taskId)),
  ), [connectionIndex, taskRects]);
  const activeTaskIds = React.useMemo(() => {
    const active = new Set(selectedTaskIds);
    if (hoveredTaskId) active.add(hoveredTaskId);
    return active;
  }, [hoveredTaskId, selectedTaskIds]);
  const displayedConnections = React.useMemo(() => (
    connectionVisibility === 'focus'
      ? getFocusedGanttV5Connections(visibleConnections, activeTaskIds)
      : visibleConnections
  ), [activeTaskIds, connectionVisibility, visibleConnections]);
  const connectionTaskIds = React.useMemo(() => new Set([
    ...displayedConnections.dependencies.flatMap((dependency) => [dependency.from, dependency.to]),
    ...displayedConnections.links.flatMap((link) => link.taskIds),
  ]), [displayedConnections]);
  const connectionAnchorRects = React.useMemo(() => {
    const modelAnchors = buildGanttV5ConnectionAnchorRects(
      model,
      connectionTaskIds,
      taskRects,
      scale,
      viewport,
      rowHeight,
    );
    const externalAnchors = buildGanttV5ExternalConnectionAnchorRects(
      model,
      displayedConnections.dependencies,
      displayedConnections.links,
      [...taskRects, ...modelAnchors],
      scale,
      viewport,
    );
    return [...modelAnchors, ...externalAnchors];
  }, [connectionTaskIds, displayedConnections, model, rowHeight, scale, taskRects, viewport]);
  const hoveredRect = React.useMemo(
    () => taskRects.find((rect) => rect.taskId === hoveredTaskId) ?? null,
    [hoveredTaskId, taskRects],
  );
  const relatedTaskIds = React.useMemo(() => {
    return getRelatedVisibleGanttV5TaskIds(
      connectionIndex,
      activeTaskIds,
      new Set(taskRects.map((rect) => rect.taskId)),
    );
  }, [activeTaskIds, connectionIndex, taskRects]);

  React.useLayoutEffect(() => {
    if (!tooltipRef.current || !hoveredRect) return;
    tooltipRef.current.style.setProperty('--gantt-v5-tooltip-x', `${Math.max(8, Math.min(viewport.width - 280, hoveredRect.x + hoveredRect.width / 2))}px`);
    tooltipRef.current.style.setProperty('--gantt-v5-tooltip-y', `${Math.max(GANTT_V5_HEADER_HEIGHT + 8, hoveredRect.y - 8)}px`);
  }, [hoveredRect, viewport.width]);

  const drawMeasured = React.useCallback((
    layer: 'static' | 'tasks' | 'interaction',
    draw: () => void,
  ) => {
    const start = performance.now();
    draw();
    onLayerRender?.(layer, performance.now() - start);
  }, [onLayerRender]);

  React.useLayoutEffect(() => {
    const canvas = staticCanvasRef.current;
    const root = rootRef.current;
    if (!canvas || !root || viewport.width <= 0 || viewport.height <= 0) return;
    const theme = readGanttV5CanvasTheme(root);
    drawMeasured('static', () => {
      drawGanttV5StaticLayer(
        canvas,
        theme,
        ticks,
        viewport,
        rowHeight,
        model.rows.length,
        todayHour === undefined ? undefined : scale.hourToX(todayHour),
      );
    });
  }, [drawMeasured, model.rows.length, rootRef, rowHeight, scale, ticks, todayHour, viewport]);

  React.useLayoutEffect(() => {
    const canvas = taskCanvasRef.current;
    const root = rootRef.current;
    if (!canvas || !root || viewport.width <= 0 || viewport.height <= 0) return;
    const theme = readGanttV5CanvasTheme(root);
    drawMeasured('tasks', () => {
      drawGanttV5TaskLayer(
        canvas,
        root,
        theme,
        taskRects,
        connectionAnchorRects,
        groupRects,
        displayedConnections.dependencies,
        displayedConnections.links,
        viewport,
      );
    });
  }, [connectionAnchorRects, displayedConnections, drawMeasured, groupRects, rootRef, taskRects, viewport]);

  React.useLayoutEffect(() => {
    const canvas = interactionCanvasRef.current;
    const root = rootRef.current;
    if (!canvas || !root || viewport.width <= 0 || viewport.height <= 0) return;
    const theme = readGanttV5CanvasTheme(root);
    drawMeasured('interaction', () => {
      drawGanttV5InteractionLayer(
        canvas,
        theme,
        taskRects,
        selectedTaskIds,
        hoveredTaskId,
        relatedTaskIds,
        previewSpans,
        scale,
        viewport,
      );
    });
  }, [drawMeasured, hoveredTaskId, previewSpans, relatedTaskIds, rootRef, scale, selectedTaskIds, taskRects, viewport]);

  const hitFromEvent = React.useCallback((
    event: React.PointerEvent<HTMLCanvasElement> | React.MouseEvent<HTMLCanvasElement>,
  ) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    return hitTestGanttV5Task(taskRects, event.clientX - bounds.left, event.clientY - bounds.top);
  }, [taskRects]);

  const groupHitFromEvent = React.useCallback((
    event: React.PointerEvent<HTMLCanvasElement> | React.MouseEvent<HTMLCanvasElement>,
  ) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    return hitTestGanttV5Group(groupRects, event.clientX - bounds.left, event.clientY - bounds.top);
  }, [groupRects]);

  return (
    <div className="wxb-gantt-v5-canvas-stack">
      <canvas ref={staticCanvasRef} className="wxb-gantt-v5-canvas wxb-gantt-v5-canvas--static" aria-hidden="true" />
      <canvas ref={taskCanvasRef} className="wxb-gantt-v5-canvas wxb-gantt-v5-canvas--tasks" aria-hidden="true" />
      <canvas
        ref={interactionCanvasRef}
        className="wxb-gantt-v5-canvas wxb-gantt-v5-canvas--interaction"
        role="application"
        tabIndex={0}
        aria-label="甘特时间轴。使用方向键切换任务，回车打开任务，空格选择，Delete 删除，Shift 加 F10 打开操作菜单，Control 或 Command 加 Z 撤销。"
        onPointerDown={(event) => onPointerDown(event, hitFromEvent(event), groupHitFromEvent(event))}
        onPointerMove={(event) => onPointerMove(event, hitFromEvent(event))}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onPointerLeave={onPointerLeave}
        onDoubleClick={(event) => onDoubleClick(event, hitFromEvent(event))}
        onContextMenu={(event) => onContextMenu(event, hitFromEvent(event), groupHitFromEvent(event))}
        onKeyDown={onKeyDown}
        onWheel={onWheel}
      />
      {hoveredRect && (
        <div ref={tooltipRef} className="wxb-gantt-v5-task-tooltip" role="tooltip">
          {hoveredRect.task.tooltip ?? (
            <>
              <strong>{hoveredRect.task.label}</strong>
              <span>{hoveredRect.task.start.toFixed(2)} – {hoveredRect.task.end.toFixed(2)} h</span>
              <span>状态 {hoveredRect.task.status ?? '—'} · 人员 {hoveredRect.task.requiredPeople ?? '—'}</span>
            </>
          )}
        </div>
      )}
    </div>
  );
};
